import {useGetGamesInfiniteQuery} from "@/lib/api/games/gameApi";
import {Link} from '@/i18n/navigation';
import React, {useMemo} from "react";
import NothingFound from "@/app/components/common/nothing-found/NothingFound";
import GameCard from "@/app/components/game/game-card/GameCard";
import {Skeleton} from "@/app/components/common/app-skeleton/AppSkeleton";
import {MainCtaBtn} from "@/app/components/common/main-cta-btn/MainCtaBtn";
import {useAppSelector} from "@/lib/hooks";
import {AuthTypesEnum} from "@/lib/enums/AuthTypesEnum";
import {useTranslations} from "next-intl";
import {calcAmountOfCards} from "@/lib/utils/functions/calcAmountOfCards";
import {TailwindBreakpointsEnum} from "@/lib/enums/TailwindBreakpointsEnum";
import {useOpenDialog} from "@/lib/dialog-orchestrator/react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {IGame} from "@/lib/types/games/IGame";
import {rememberGameOpenSource} from "@/lib/analytics/mixpanel-events";
import {useLobbyCountry} from "@/hooks/use-lobby-country";

interface IProps {
    search: string;
}

const amountOfCards = calcAmountOfCards({
    [TailwindBreakpointsEnum.XS]: 36,
    [TailwindBreakpointsEnum.SM]: 36,
    [TailwindBreakpointsEnum.MD]: 35,
    [TailwindBreakpointsEnum.LG]: 36,
    [TailwindBreakpointsEnum.XL]: 35,
    [TailwindBreakpointsEnum.TWO_XL]: 32,
})

// const AuthDialog = dynamic(() => import("@/app/components/auth/AuthDialog"));

export const LobbyExpandedTab = ({search}: IProps) => {
    const t = useTranslations();
    const openDialog = useOpenDialog();
    const isAuthorized = useAppSelector(state => !!state.auth.userId);
    const userCountry = useAppSelector(state => state.auth.country);
    const lobbyCountry = useLobbyCountry();

    const {
        data: games,
        isFetching: gamesFetching,
        hasNextPage: gamesHasNextPage,
        fetchNextPage: gamesFetchNextPage
    } = useGetGamesInfiniteQuery({
        title: search.trim(),
        size: amountOfCards,
        country: lobbyCountry,
    })

    const allGames = useMemo(() => {
        return games?.pages?.flatMap(page => page.content || []) || [];
    }, [games]);

    const isNoRecords = useMemo(() => {
        return !allGames.length && !gamesFetching;
    }, [allGames.length, gamesFetching])

    const isCardGated = (item: IGame) => !isAuthorized || item.producer.restrictedCountries.includes(userCountry);

    const onCardClick = (event: React.MouseEvent<HTMLAnchorElement>, item: IGame) => {
        if (!isCardGated(item)) {
            // The game page emits `game_opened`; it can only know which grid the
            // player came from if the grid says so on its way out.
            rememberGameOpenSource("casino");
            return;
        }

        event.preventDefault();

        if (!isAuthorized) {
            openDialog(DialogsEnum.AUTH, {props: {tab: AuthTypesEnum.REGISTER}});
        }
    }
    return (
        <>
            {
                isNoRecords ? <NothingFound/> : (
                    <>
                        <div
                            className={'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-[16px] mb-[16px]'}>
                            {allGames.map((item) => (
                                <Link
                                    key={item.id}
                                    href={isCardGated(item) ? '#' : `/games/${item.id}`}
                                    onClick={(event) => onCardClick(event, item)}>
                                    <GameCard
                                        isUnavailable={item.producer.restrictedCountries.includes(userCountry)}
                                        poster={item.previewUrl}
                                        sizes={'(max-width: 639px) 30vw, (max-width: 767px) 23vw, (max-width: 1023px) 18vw, (max-width: 1279px) 15vw, (max-width: 1535px) 13vw, 12vw'}
                                        gameName={item.title}
                                        providerName={item.producer.title}
                                    />
                                </Link>
                            ))}
                            {
                                gamesFetching ? Array.from({length: 36}).map((_, index) => (
                                    <Skeleton key={index} className={'aspect-3/4 bg-noble-black-600'}/>
                                )) : null
                            }
                        </div>

                        {
                            gamesHasNextPage && (
                                <MainCtaBtn onClick={gamesFetchNextPage}
                                            className={'mx-auto block mt-[32px] w-[280px] h-[41px] py-[12px]'}>
                                    {t('AppBtn.loadMore')}
                                </MainCtaBtn>
                            )
                        }
                    </>
                )
            }
        </>
    )
}
